import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { 
  Zap,
  ClipboardCheck,
  AlertCircle,
  TrendingUp,
  Library,
  Newspaper,
  ChevronRight,
  Calendar,
  MessageSquare,
  FileText,
  Star,
  Activity,
  Target,
  BookOpen
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

const BOOK_URL = 'https://customer-assets.emergentagent.com/job_player-connect-ikons/artifacts/67uo9q3e_Libro_Marca_Personal_Deportiva_por_Edgar_Merino.pdf';

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ events: 0, messages: 0, news: 0, resources: 0 });
  const [ikonsData, setIkonsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const promises = [
        api.get('/events'),
        api.get('/news'),
        api.get('/messages/conversations'),
        api.get('/resources')
      ];
      
      if (user?.role === 'player') {
        promises.push(api.get('/ikons/dashboard'));
      }

      const results = await Promise.all(promises);
      
      setStats({
        events: results[0].data.length,
        news: results[1].data.length,
        messages: results[2].data.length,
        resources: results[3].data.length
      });

      if (user?.role === 'player' && results[4]) {
        setIkonsData(results[4].data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookDownload = () => {
    // Open the book immediately (synchronous) to avoid popup blockers
    window.open(BOOK_URL, '_blank');
    
    // Track download in background (don't await)
    api.post('/book-downloads/track', {}).catch(() => {});
  };

  const getReadinessScore = () => {
    if (!ikonsData?.latest_performance_map) return null;
    const map = ikonsData.latest_performance_map;
    const total = (map.physical_energy || 0) + (map.mental_clarity || 0) + 
                  (map.emotional_state || 0) + (map.confidence || 0) + 
                  (map.competitive_level || 0);
    return Math.round((total / 25) * 100);
  };

  const readiness = getReadinessScore();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Player Dashboard
  if (user?.role === 'player') {
    return (
      <Layout>
        <div className="px-4 lg:px-8 py-6 max-w-4xl mx-auto" data-testid="player-dashboard">
          {/* Welcome */}
          <div className="mb-6">
            <p className="text-[#808080] text-sm">Bienvenido</p>
            <h1 className="text-2xl font-bold text-[#4F4F4F]">{user?.name?.split(' ')[0]}</h1>
          </div>

          {/* Quick Actions */}
          <div className="mb-6">
            <p className="text-xs text-[#808080] uppercase tracking-wider mb-3">Acciones Rápidas</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/ikons/pre-match')}
                data-testid="quick-pre-match"
                className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-2">
                  <Target className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="font-semibold text-[#4F4F4F]">Pre-Partido</p>
                <p className="text-xs text-[#808080]">Plan + Mapa 360°</p>
              </button>

              <button
                onClick={() => navigate('/ikons/post-match')}
                data-testid="quick-post-match"
                className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                  <Activity className="w-6 h-6 text-blue-500" />
                </div>
                <p className="font-semibold text-[#4F4F4F]">Post-Partido</p>
                <p className="text-xs text-[#808080]">Recuperación</p>
              </button>

              <button
                onClick={() => navigate('/monthly-checkin')}
                data-testid="quick-checkin"
                className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                  <ClipboardCheck className="w-6 h-6 text-green-500" />
                </div>
                <p className="font-semibold text-[#4F4F4F]">Check-in</p>
                <p className="text-xs text-[#808080]">Mensual</p>
              </button>

              <button
                onClick={() => navigate('/report-injury')}
                data-testid="quick-injury"
                className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-red-200 active:scale-95 transition-all"
              >
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-2">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <p className="font-semibold text-[#4F4F4F]">Lesión</p>
                <p className="text-xs text-[#808080]">Reportar</p>
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <button 
              onClick={() => navigate('/resources')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm text-center hover:border-[#D4AF37]/50 transition-all"
            >
              <Library className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-xl font-bold text-[#4F4F4F]">{stats.resources}</p>
              <p className="text-xs text-[#808080]">Recursos</p>
            </button>
            <button 
              onClick={() => navigate('/news')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm text-center hover:border-[#D4AF37]/50 transition-all"
            >
              <Newspaper className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-xl font-bold text-[#4F4F4F]">{stats.news}</p>
              <p className="text-xs text-[#808080]">Noticias</p>
            </button>
            <button 
              onClick={() => navigate('/messages')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm text-center hover:border-[#D4AF37]/50 transition-all"
            >
              <MessageSquare className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-xl font-bold text-[#4F4F4F]">{stats.messages}</p>
              <p className="text-xs text-[#808080]">Mensajes</p>
            </button>
          </div>

          {/* Book Banner */}
          <button
            onClick={handleBookDownload}
            className="w-full p-4 bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl border border-[#D4AF37]/20 flex items-center gap-4 mb-4 hover:border-[#D4AF37]/40 transition-all"
          >
            <div className="w-12 h-12 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-[#4F4F4F]">Marca Personal Deportiva</p>
              <p className="text-xs text-[#808080]">por Edgar Merino • PDF Gratis</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
          </button>

          {/* Weekly Plan CTA */}
          <button
            onClick={() => navigate('/ikons/weekly')}
            className="w-full p-4 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center gap-4 hover:border-[#D4AF37]/50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-[#4F4F4F] flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-[#4F4F4F]">Plan Semanal</p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#808080]" />
          </button>
        </div>
      </Layout>
    );
  }

  // Agent Dashboard
  return (
    <Layout>
      <div className="px-4 lg:px-8 py-6 max-w-4xl mx-auto" data-testid="agent-dashboard">
        {/* Welcome */}
        <div className="mb-6">
          <p className="text-[#808080] text-sm">Panel de Agente</p>
          <h1 className="text-2xl font-bold text-[#4F4F4F]">{user?.name}</h1>
        </div>

        {/* Quick Actions for Agent */}
        <div className="mb-6">
          <p className="text-xs text-[#808080] uppercase tracking-wider mb-3">Acciones Rápidas</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/ikons/admin')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-2">
                <Star className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <p className="font-semibold text-[#4F4F4F]">Panel Jugadores</p>
              <p className="text-xs text-[#808080]">Ver Estado</p>
            </button>

            <button
              onClick={() => navigate('/user-management')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
              <p className="font-semibold text-[#4F4F4F]">Gestión Usuarios</p>
              <p className="text-xs text-[#808080]">Aprobar / Rechazar</p>
            </button>

            <button
              onClick={() => navigate('/resources')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                <Library className="w-6 h-6 text-green-500" />
              </div>
              <p className="font-semibold text-[#4F4F4F]">Recursos</p>
              <p className="text-xs text-[#808080]">Biblioteca</p>
            </button>

            <button
              onClick={() => navigate('/news')}
              className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#D4AF37]/50 active:scale-95 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-2">
                <Newspaper className="w-6 h-6 text-purple-500" />
              </div>
              <p className="font-semibold text-[#4F4F4F]">Noticias</p>
              <p className="text-xs text-[#808080]">Gestionar</p>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs text-[#808080] uppercase">Recursos</p>
            <p className="text-2xl font-bold text-[#4F4F4F]">{stats.resources}</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs text-[#808080] uppercase">Noticias</p>
            <p className="text-2xl font-bold text-[#4F4F4F]">{stats.news}</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
